import type { WorldSaveData } from '../world/World';
import type { PlayerSave } from '../player/Player';
import type { TimeState } from '../core/Time';
import type { WeatherSave } from '../systems/Weather';

/**
 * Kayıt sistemi.
 *
 * Güvenlik önlemleri:
 *  1. ATOMİK YAZIM: veri önce ".tmp" anahtarına yazılır, geri okunup JSON olarak
 *     doğrulanır, ancak ondan sonra asıl anahtara taşınır. Yazma yarıda kesilirse
 *     eski kayıt bozulmamış kalır.
 *  2. YEDEK: asıl kayıt üzerine yazılmadan önce ".bak" olarak kopyalanır.
 *     Asıl kayıt okunamazsa yedekten dönülür.
 *  3. SÜRÜMLEME: her kayıt version taşır; migration zinciri eski kayıtları yükseltir.
 *  4. KOTA: localStorage dolduğunda anlaşılır hata döner, sessizce veri kaybı olmaz.
 */

export const SAVE_VERSION = 3;
const PREFIX = 'kg.world.';
const INDEX_KEY = 'kg.worlds';
const PROFILE_KEY = 'kg.profile';

export interface WorldMeta {
  id: string;
  name: string;
  mode: 'story' | 'normal';
  difficulty: string;
  seed: number;
  createdAt: number;
  updatedAt: number;
  playtime: number;
  day: number;
}

export interface GameSave {
  version: number;
  meta: WorldMeta;
  world: WorldSaveData;
  player: PlayerSave;
  time: TimeState;
  weather?: WeatherSave;
  quests: unknown;
  economy: unknown;
  achievements: string[];
}

export interface ProfileData {
  name: string;
  avatar: string | null;
  stats: {
    playtime: number;
    worldsCreated: number;
    discovered: number;
    fishCaught: number;
    oresMined: number;
    bossesKilled: number;
    deaths: number;
    kogoxEarned: number;
  };
  achievements: string[];
}

export type SaveResult = { ok: true } | { ok: false; error: string };

export class SaveManager {
  /** Dünya listesi (bozuk kayıtlar ayıklanır). */
  listWorlds(): WorldMeta[] {
    const raw = this.readJson<WorldMeta[]>(INDEX_KEY);
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((m): m is WorldMeta => !!m && typeof m.id === 'string' && typeof m.name === 'string')
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  }

  hasWorld(id: string): boolean {
    return localStorage.getItem(PREFIX + id) !== null;
  }

  /** Kayıt yazımı: tmp -> doğrula -> bak -> asıl. */
  save(save: GameSave): SaveResult {
    const key = PREFIX + save.meta.id;
    const tmpKey = `${key}.tmp`;
    const bakKey = `${key}.bak`;
    save.version = SAVE_VERSION;
    save.meta.updatedAt = Date.now();

    let payload: string;
    try {
      payload = JSON.stringify(save);
    } catch (err) {
      return { ok: false, error: `Kayıt serileştirilemedi: ${String(err)}` };
    }

    try {
      localStorage.setItem(tmpKey, payload);
    } catch (err) {
      const quota = err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22);
      return { ok: false, error: quota ? 'Depolama alanı dolu. Eski dünyaları silmeyi deneyin.' : `Yazma hatası: ${String(err)}` };
    }

    // Geri okuyup doğrula: yarı yazılmış veriyi asla asıl kayda taşımayız
    const verify = localStorage.getItem(tmpKey);
    if (!verify || verify.length !== payload.length) {
      localStorage.removeItem(tmpKey);
      return { ok: false, error: 'Kayıt doğrulaması başarısız (eksik yazım).' };
    }
    try {
      JSON.parse(verify);
    } catch {
      localStorage.removeItem(tmpKey);
      return { ok: false, error: 'Kayıt doğrulaması başarısız (bozuk JSON).' };
    }

    // Mevcut kaydı yedekle, sonra taşı
    const current = localStorage.getItem(key);
    try {
      if (current) localStorage.setItem(bakKey, current);
      localStorage.setItem(key, verify);
      localStorage.removeItem(tmpKey);
    } catch (err) {
      return { ok: false, error: `Kayıt taşınamadı: ${String(err)}` };
    }

    this.updateIndex(save.meta);
    return { ok: true };
  }

  /** Kaydı okur; asıl kayıt bozuksa yedeğe düşer. */
  load(id: string): { save: GameSave | null; recovered: boolean; error?: string } {
    const key = PREFIX + id;
    const main = this.tryParseSave(localStorage.getItem(key));
    if (main) return { save: main, recovered: false };

    const bak = this.tryParseSave(localStorage.getItem(`${key}.bak`));
    if (bak) {
      console.warn(`[Save] "${id}" asıl kayıt okunamadı, yedekten geri yüklendi.`);
      return { save: bak, recovered: true };
    }
    return { save: null, recovered: false, error: 'Kayıt okunamadı veya bozuk.' };
  }

  deleteWorld(id: string): void {
    const key = PREFIX + id;
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}.bak`);
    localStorage.removeItem(`${key}.tmp`);
    const list = this.listWorlds().filter((m) => m.id !== id);
    this.writeJson(INDEX_KEY, list);
  }

  private updateIndex(meta: WorldMeta): void {
    const list = this.listWorlds().filter((m) => m.id !== meta.id);
    list.push(meta);
    this.writeJson(INDEX_KEY, list);
  }

  private tryParseSave(raw: string | null): GameSave | null {
    if (!raw) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.warn('[Save] JSON çözümlenemedi:', err);
      return null;
    }
    const migrated = migrate(parsed);
    return migrated;
  }

  // ------------------------------------------------------------ profil
  loadProfile(): ProfileData {
    const raw = this.readJson<Partial<ProfileData>>(PROFILE_KEY);
    const s: Partial<ProfileData['stats']> = raw?.stats ?? {};
    return {
      name: typeof raw?.name === 'string' && raw.name.trim() ? raw.name.slice(0, 24) : 'Gezgin',
      avatar: typeof raw?.avatar === 'string' ? raw.avatar : null,
      stats: {
        playtime: numOr(s.playtime, 0),
        worldsCreated: numOr(s.worldsCreated, 0),
        discovered: numOr(s.discovered, 0),
        fishCaught: numOr(s.fishCaught, 0),
        oresMined: numOr(s.oresMined, 0),
        bossesKilled: numOr(s.bossesKilled, 0),
        deaths: numOr(s.deaths, 0),
        kogoxEarned: numOr(s.kogoxEarned, 0),
      },
      achievements: Array.isArray(raw?.achievements) ? raw!.achievements!.filter((a) => typeof a === 'string') : [],
    };
  }

  saveProfile(profile: ProfileData): SaveResult {
    try {
      // Avatar base64 çok büyükse kaydı reddetme yerine avatarı düşür (kayıt kaybı olmasın)
      let toWrite = profile;
      if (profile.avatar && profile.avatar.length > 400_000) {
        toWrite = { ...profile, avatar: null };
        console.warn('[Save] Profil fotoğrafı çok büyük, kaydedilmedi.');
      }
      localStorage.setItem(PROFILE_KEY, JSON.stringify(toWrite));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: `Profil kaydedilemedi: ${String(err)}` };
    }
  }

  private readJson<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch (err) {
      console.warn(`[Save] "${key}" okunamadı:`, err);
      return null;
    }
  }

  private writeJson(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      console.warn(`[Save] "${key}" yazılamadı:`, err);
    }
  }

  /** Yaklaşık kullanılan alan (KB) — ayarlar ekranında gösterilir. */
  usageKB(): number {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('kg.')) continue;
      total += k.length + (localStorage.getItem(k)?.length ?? 0);
    }
    return Math.round(total / 512) / 2;
  }
}

function numOr(v: unknown, def: number): number {
  return Number.isFinite(v as number) ? (v as number) : def;
}

/**
 * Sürüm yükseltme zinciri.
 * Eski kayıtlar okunabilir kalır; her adım yalnızca bir sürüm ilerletir.
 */
function migrate(raw: unknown): GameSave | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, any>;
  let version = Number.isFinite(data.version) ? (data.version as number) : 1;

  if (version < 2) {
    // v1 -> v2: quests/economy alanları eklendi
    data.quests ??= null;
    data.economy ??= null;
    version = 2;
  }
  if (version < 3) {
    // v2 -> v3: achievements listesi ve meta.playtime eklendi
    data.achievements ??= [];
    if (data.meta && !Number.isFinite(data.meta.playtime)) data.meta.playtime = data.player?.playtime ?? 0;
    version = 3;
  }
  data.version = version;

  // Zorunlu alanların varlığını doğrula
  if (!data.meta || typeof data.meta.id !== 'string') return null;
  if (!data.world || typeof data.world !== 'object') return null;
  if (!data.player || typeof data.player !== 'object') return null;
  data.time ??= { elapsed: 0, day: 1 };
  data.achievements = Array.isArray(data.achievements) ? data.achievements.filter((a: unknown) => typeof a === 'string') : [];

  return data as unknown as GameSave;
}
